import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsIcon from "@mui/icons-material/Settings";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import HistoryIcon from "@mui/icons-material/History";
import axios from "axios";

import PageLoader from "../Components/PageLoader";
import Overview from "../Components/Overview";
import DBConnection from "../Components/DBConnection";
import Settings from "../Components/Settings";
import DataBackup from "../Components/DataBackup";
import HistoricalData from "../Components/HistoricalData/HistoricalData";
import {
  BRAND_COLOR_TEXT,
  BRAND_COLOR_TINT,
  CARD_BORDER,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from "../utils/colors";
import { isConnectionVerified } from "../utils/api";
import { APP_NAME, APP_NAME_SUFFIX } from "../utils/constants";
import {
  FS_BODY,
  FS_CAPTION,
  FS_TITLE,
  FW_REGULAR,
  FW_SEMIBOLD,
} from "../utils/typography";

const ZOHO = window.ZOHO;

const SIDEBAR_WIDTH = 230;

const NAV_ITEMS = [
  { label: "Authentication", icon: <VpnKeyIcon /> },
  { label: "DB Connection", icon: <StorageIcon /> },
  { label: "Settings", icon: <SettingsIcon /> },
  { label: "Backup / Restore", icon: <CloudSyncIcon /> },
  { label: "Historical Data", icon: <HistoryIcon /> },
];

export default function SettingsRoute() {
  const [authLoadingZoho, setAuthLoadingZoho] = useState(false);
  const [revokeLoadingZoho, setRevokeLoadingZoho] = useState(false);
  const [zohoAuth, setZohoAuth] = useState(false);
  const [connectionUrl, setConnectionUrl] = useState(null);
  // A database connection that has actually VERIFIED — not merely a saved URL.
  // Gating keys off this so a stored-but-broken connection still locks the
  // downstream tabs instead of letting the user into a dead Settings tab.
  const [dbConnected, setDbConnected] = useState(false);
  const [disabledTabs, setDisabledTabs] = useState({
    dbTab: false,
    settingsTab: false,
    backupData: false,
    historical: false,
  });

  const [snackbarMsg, setSnackbarMsg] = useState("");

  const [snackBarForError, setSnackBarForError] = useState(false);

  const [tabNumber, setTabNumber] = useState(null);
  const [zohoLoaded, setZohoLoaded] = useState(false);

  const [loading, setLoading] = useState(true);

  const [apiKey, setApiKey] = useState();

  const [orgId, setOrgId] = useState();
  const [zapiKey, setZapiKey] = useState();
  const [dataCenterUrl, setDataCenterUrl] = useState("");
  const [zuid, setZuid] = useState(null);

  const handleAuthCheckZoho = async ({ headers }) => {
    // for zoho auth verify
    // setAuthLoadingZoho(true);

    const authCheck = await axios({
      url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/auth/zoho/verifyauth`,
      headers: headers,
    });

    if (authCheck?.data?.org?.length > 0) {
      // setAuthLoadingZoho(false);
      return true;
    } else {
      // setAuthLoadingZoho(false);
      return false;
    }
  };

  useEffect(() => {
    async function initZoho() {
      ZOHO.embeddedApp.on("PageLoad", async function (data) {
        const currentEnv = await ZOHO.CRM.CONFIG.GetCurrentEnvironment();
        const dataCenterMap = {
          US: "https://www.zohoapis.com",
          EU: "https://www.zohoapis.eu",
          AU: "https://www.zohoapis.com.au",
          IN: "https://www.zohoapis.in",
          China: "https://www.zohoapis.com.cn",
          JP: "https://www.zohoapis.jp",
        };
        let dataCenterUrl = dataCenterMap?.[currentEnv?.deployment] || "";
        if (process.env.REACT_APP_SANDBOX === "true") {
          dataCenterUrl = "https://plugin-easypostgresqlsync.zohosandbox.com";
        }

        setDataCenterUrl(dataCenterUrl);

        const dataCenterData = {
          apiname:
            process.env.REACT_APP_EXTENSION_IDENTIFIER + "__Data_Center_URL",
          value: dataCenterUrl,
        };

        await ZOHO.CRM.CONNECTOR.invokeAPI("crm.set", dataCenterData);
        setZohoLoaded(true);
      });
      ZOHO.embeddedApp.init();
    }
    initZoho();
  }, []);

  useEffect(() => {
    if (zohoLoaded) {
      // fetch previous saved data for external service and zoho crm
      fetchOrgVariablesData()
        .then(async (result) => {
          const { authenticated, apiKey, orgid: orgId, connectionUrl } =
            result || {};

          // Authentication is established by verifyauth inside
          // fetchOrgVariablesData — NOT by whether a database happens to
          // verify. Tying the two together locked authenticated users with no
          // database out of the DB Connection tab entirely.
          if (!authenticated) return;
          setZohoAuth(true);

          // No saved connection: nothing to verify. Skip the round trip —
          // posting Connection_Url:"null" just asks the backend to fail.
          if (!connectionUrl) {
            setConnectionUrl(null);
            setDbConnected(false);
            setTabNumber(1);
            return;
          }

          try {
            const testConnUrl = await axios.request({
              url: `${process.env.REACT_APP_DBURL}/verify`,
              method: "POST",
              headers: {
                orgid: orgId, // Org ID
                apikey: apiKey, // API KEy
                connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
              },
              data: {
                Connection_Url: connectionUrl,
              },
            });

            // The response BODY is `{data: true}` on success and
            // `{data: false, error}` on failure — both objects, both truthy.
            // Testing `testConnUrl.data` therefore passes on failure.
            if (isConnectionVerified(testConnUrl?.data)) {
              setConnectionUrl(connectionUrl);
              setDbConnected(true);
              setTabNumber(2);
            } else {
              setConnectionUrl(null);
              setDbConnected(false);
              setTabNumber(1);
            }
          } catch (error) {
            setConnectionUrl(null);
            setDbConnected(false);
            setTabNumber(1);
          }
        })
        .catch(() => {
          // Never leave the user staring at a loader.
          setZohoAuth(false);
          setDbConnected(false);
          setTabNumber(0);
        })
        .then(() => {
          setLoading(false);
        });
    }
  }, [zohoLoaded]);

  const dbConnectionCheck = async () => {
    try {
      const testConnUrl = await axios.request({
        url: `${process.env.REACT_APP_DBURL}/verify`,
        method: "POST",
        headers: {
          orgid: orgId, // Org ID
          apikey: apiKey, // API KEy
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
        },
        data: {
          Connection_Url: connectionUrl,
        },
      });
      if (isConnectionVerified(testConnUrl?.data)) {
        setDbConnected(true);
        setTabNumber(2);
      } else {
        setDbConnected(false);
        setTabNumber(1);
      }
    } catch (error) {
      setDbConnected(false);
      setTabNumber(1);
    }
  };

  const handleAuthenticateZoho = async () => {
    setAuthLoadingZoho(true);

    const authUrlResp = await axios.request({
      url: process.env.REACT_APP_ADMIN_SERVER_URL + "/auth/zoho/authenticate",
      method: "POST",
      data: {
        orgId: orgId,
        apiKey: apiKey,
        zapiKey: zapiKey,
        accountsUrl:
          "https://accounts.zoho." +
          dataCenterUrl?.split("https://www.zohoapis.")?.[1],
        connName: process.env.REACT_APP_EXTENSION_IDENTIFIER + "__zoho",
        scope: [
          // "ZohoCRM.users.ALL",
          "ZohoCRM.users.CREATE",
          "ZohoCRM.users.READ",
          "ZohoCRM.users.UPDATE",
          "ZohoCRM.org.READ",
          "ZohoCRM.org.UPDATE",
          "ZohoCRM.settings.CREATE",
          "ZohoCRM.settings.READ",
          "ZohoCRM.settings.UPDATE",
          "ZohoCRM.coql.READ",
          "ZohoCRM.modules.CREATE",
          "ZohoCRM.modules.READ",
          "ZohoCRM.modules.UPDATE",
        ],
        redirectUrl: `${process.env.REACT_APP_ADMIN_SERVER_URL}/auth/zoho/callback`,
        crmApiDomain: dataCenterUrl,
        zuid: zuid,
      },
    });

    const myAuthCheckInterval = setInterval(async () => {
      const authCheckStatus = await handleAuthCheckZoho({
        headers: {
          apikey: apiKey,
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER + "__zoho",
          orgid: orgId,
          accountsurl: dataCenterUrl,
          dataCenterurlvariablename:
            process.env.REACT_APP_EXTENSION_IDENTIFIER + "__Data_Center_URL",
        },
        // dataCenterUrl: dataCenterUrl,
      });

      if (authCheckStatus) {
        // setTabNumber(1);
        setZohoAuth(true);
        setAuthLoadingZoho(false);
        clearInterval(myAuthCheckInterval);
        dbConnectionCheck();
      } else {
        // setAuthLoadingZoho(false);
      }
    }, 5000);

    const newwindow = window.open(
      authUrlResp?.data?.authUrl,
      "windowName",
      "height=640,width=1024"
    );
    if (window.focus) {
      newwindow.focus();
    }
  };

  const handleRevokeZoho = async () => {
    setRevokeLoadingZoho(true);

    const authUrlResp = await axios.request({
      url: process.env.REACT_APP_ADMIN_SERVER_URL + "/auth/zoho/revoke",
      method: "GET",
      headers: {
        orgid: orgId,
        apikey: apiKey,
        connname: process.env.REACT_APP_EXTENSION_IDENTIFIER + "__zoho",
      },
    });

    if (authUrlResp?.data) {
      setZohoAuth(false);
      setRevokeLoadingZoho(false);
    } else {
      setRevokeLoadingZoho(false);
    }
  };

  const fetchOrgVariablesData = async () => {
    /*
      Get Organization Variables
    */

    const orgData = {
      apiKeys: [
        process.env.REACT_APP_EXTENSION_IDENTIFIER + "__API_KEY",
        process.env.REACT_APP_EXTENSION_IDENTIFIER + "__Organization_ID",
        process.env.REACT_APP_EXTENSION_IDENTIFIER + "__ZAPI_Key",
        process.env.REACT_APP_EXTENSION_IDENTIFIER + "__Data_Center_URL",
        process.env.REACT_APP_EXTENSION_IDENTIFIER + "__DB_Connection_URL",
      ],
    };

    const orgVariables = await ZOHO.CRM.API.getOrgVariable(orgData);

    setDataCenterUrl(
      orgVariables?.Success?.Content?.[
        `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
      ]?.value
        ? orgVariables?.Success?.Content?.[
            `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
          ]?.value != "null"
          ? orgVariables?.Success?.Content?.[
              `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
            ]?.value
          : dataCenterUrl
        : dataCenterUrl
    );

    setApiKey(
      orgVariables?.Success?.Content?.[
        `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__API_KEY`
      ]?.value
    );
    // Zoho stores "unset" as the literal four-character string "null", which
    // is truthy in JS — normalise it or a disconnected org reads as connected.
    const savedDbUrl =
      orgVariables?.Success?.Content?.[
        `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__DB_Connection_URL`
      ]?.value;
    setConnectionUrl(
      !savedDbUrl || savedDbUrl === "null" ? null : savedDbUrl
    );

    setOrgId(
      orgVariables?.Success?.Content?.[
        `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Organization_ID`
      ]?.value
    );

    setZapiKey(
      orgVariables?.Success?.Content?.[
        `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__ZAPI_Key`
      ]?.value
    );

    const headersZoho = {
      apikey:
        orgVariables?.Success?.Content?.[
          `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__API_KEY`
        ]?.value,
      connname: `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__zoho`,
      orgid:
        orgVariables?.Success?.Content?.[
          `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Organization_ID`
        ]?.value,
      accountsurl: orgVariables?.Success?.Content
        ?.easygoogledriveforcrm__Data_Center_URL?.value
        ? orgVariables?.Success?.Content?.[
            `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
          ]?.value != "null"
          ? orgVariables?.Success?.Content?.[
              `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
            ]?.value
          : dataCenterUrl
        : dataCenterUrl,
      dataCenterurlvariablename: `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`,
    };

    /**
     * Fetch User Details
     */
    const orgResp = await ZOHO.CRM.CONFIG.getOrgInfo();

    setZuid(orgResp?.org?.[0]?.primary_zuid);

    const authCheckStatusZoho = await handleAuthCheckZoho({
      headers: headersZoho,
      dataCenterUrl: orgVariables?.Success?.Content?.[
        `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
      ]?.value
        ? orgVariables?.Success?.Content?.[
            `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
          ]?.value != "null"
          ? orgVariables?.Success?.Content?.[
              `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
            ]?.value
          : dataCenterUrl
        : dataCenterUrl,
    });

    if (authCheckStatusZoho) {
      // Create Extension Widget
      const createExtension = await axios.request({
        url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/data/zoho/crm/extwidgets`,
        method: "POST",
        data: {
          widgetName: "PostgreSQL Sync by Easy Pluginz",
          widgetUrl:
            "https://widgets.postgresqlsync.easy-pluginz.com/relatedlist",
          widgetDescription:
            "This widget you can utilize in any Standard Module and Custom Module to check history and restore data",
          widgetType: "RELATED_LIST",
          apiDomain: dataCenterUrl,
        },
        headers: {
          apikey:
            orgVariables?.Success?.Content?.[
              `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__API_KEY`
            ]?.value,
          connname: `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__zoho`,
          datacenterurl: orgVariables?.Success?.Content?.[
            `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
          ]?.value
            ? orgVariables?.Success?.Content?.[
                `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
              ]?.value != "null"
              ? orgVariables?.Success?.Content?.[
                  `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Data_Center_URL`
                ]?.value
              : dataCenterUrl
            : dataCenterUrl,
          orgid:
            orgVariables?.Success?.Content?.[
              `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Organization_ID`
            ]?.value,
        },
      });

      //check db connection
      const savedDbUrl =
        orgVariables?.Success?.Content?.[
          `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__DB_Connection_URL`
        ]?.value;

      return {
        authenticated: true,
        apiKey:
          orgVariables?.Success?.Content?.[
            `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__API_KEY`
          ]?.value,
        orgid:
          orgVariables?.Success?.Content?.[
            `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__Organization_ID`
          ]?.value,
        connname: `${process.env.REACT_APP_EXTENSION_IDENTIFIER}__zoho`,
        // "null" is the literal string Zoho stores for "unset" — normalise it
        // here so no caller has to remember the quirk.
        connectionUrl: !savedDbUrl || savedDbUrl === "null" ? null : savedDbUrl,
      };
    }

    // Not authenticated: lock everything back to the Authentication tab.
    // Must still RETURN an object — the caller destructures this, and
    // returning undefined throws before the loader is ever cleared.
    setZohoAuth(false);
    setDbConnected(false);
    setConnectionUrl(null);
    setTabNumber(0);
    setLoading(false);
    return { authenticated: false };
  };

  // Onboarding gating (spec §5) — tabs unlock in order:
  //
  //   0  Authentication   always reachable
  //   1  DB Connection    needs Zoho auth
  //   2+ Settings /
  //      Backup / Restore /
  //      Historical Data  need auth AND a verified database
  //
  // Derived from live state rather than a separate `disabledTabs` object, so
  // revoking auth or disconnecting the database re-locks the tabs on the spot
  // instead of leaving a stale flag behind.
  const tabDisabled = (index) => {
    if (index === 0) return false;
    if (index === 1) return !zohoAuth;
    return !zohoAuth || !dbConnected;
  };

  // If the tab currently open just became unreachable — auth revoked, or the
  // database disconnected — fall back to the furthest tab still allowed
  // rather than stranding the user on a dead panel.
  useEffect(() => {
    if (tabNumber === null) return;
    if (!tabDisabled(tabNumber)) return;
    setTabNumber(zohoAuth ? 1 : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zohoAuth, dbConnected, tabNumber]);

  if (loading) {
    return <PageLoader fullScreen message="Loading Easy Postgres for Zoho CRM…" />;
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "transparent" }}>
      <Snackbar
        open={snackBarForError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={6000}
        onClose={(event, reason) => {
          if (reason === "clickaway") {
            return;
          }

          setSnackBarForError(false);
        }}
      >
        <MuiAlert
          onClose={(event, reason) => {
            if (reason === "clickaway") {
              return;
            }

            setSnackBarForError(false);
          }}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
          {/* You might be entered a wrong API KEY. */}
        </MuiAlert>
      </Snackbar>

      {/* Sidebar */}
      <Box
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          borderRight: CARD_BORDER,
          bgcolor: "transparent",
          display: "flex",
          flexDirection: "column",
          position: "sticky",
          top: 0,
          height: "100vh",
        }}
      >
        {/* Extension name as plain text — spec §5 forbids a logo image */}
        <Box sx={{ px: 2, py: 2.25, lineHeight: 1.2 }}>
          <Typography
            sx={{
              fontSize: FS_TITLE,
              fontWeight: FW_SEMIBOLD,
              color: TEXT_PRIMARY,
            }}
          >
            {APP_NAME}
          </Typography>
          <Typography sx={{ fontSize: FS_CAPTION, color: TEXT_SECONDARY }}>
            {APP_NAME_SUFFIX}
          </Typography>
        </Box>
        <List sx={{ px: 1.25, flex: 1 }}>
          {NAV_ITEMS.map((item, index) => (
            <ListItemButton
              key={item.label}
              selected={tabNumber === index}
              disabled={tabDisabled(index)}
              onClick={() => setTabNumber(index)}
              sx={{
                borderRadius: "8px",
                mb: 0.5,
                // Zoho's own nav rows are taller than MUI's default; matching
                // that is as much of the "more readable" feel as the size is.
                py: 1.1,
                "&.Mui-selected": {
                  bgcolor: BRAND_COLOR_TINT,
                  color: BRAND_COLOR_TEXT,
                  "& .MuiListItemIcon-root": { color: BRAND_COLOR_TEXT },
                  "&:hover": { bgcolor: BRAND_COLOR_TINT },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                // Zoho's own sidebar keeps idle nav labels at regular weight
                // and leans on the tint + color for the active row, so only
                // the selected item earns semibold here
                primaryTypographyProps={{
                  fontSize: FS_BODY,
                  fontWeight:
                    tabNumber === index ? FW_SEMIBOLD : FW_REGULAR,
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0, p: 3 }}>
        {/* Authentication */}
        {tabNumber === 0 && (
          <Overview
            handleRevokeZoho={handleRevokeZoho}
            revokeLoadingZoho={revokeLoadingZoho}
            authLoadingZoho={authLoadingZoho}
            zohoAuth={zohoAuth}
            handleAuthenticateZoho={handleAuthenticateZoho}
            orgId={orgId}
            dataCenterUrl={dataCenterUrl}
          />
        )}
        {tabNumber === 1 && (
          <DBConnection
            setTabNumber={setTabNumber}
            setDisabledTabs={setDisabledTabs}
            orgId={orgId}
            apiKey={apiKey}
            handleConnectionUrl={(url) => {
              // DBConnection only hands up a URL once it has verified, and
              // hands up null on failure or disconnect — so this is the
              // single signal that locks/unlocks the downstream tabs.
              setConnectionUrl(url);
              setDbConnected(Boolean(url));
            }}
          />
        )}
        {tabNumber === 2 && (
          <Settings
            orgId={orgId}
            apiKey={apiKey}
            connectionUrl={connectionUrl}
            dataCenterUrl={dataCenterUrl}
            zapiKey={zapiKey}
          />
        )}
        {tabNumber === 3 && (
          <DataBackup
            orgId={orgId}
            apiKey={apiKey}
            dataCenterUrl={dataCenterUrl}
            zapiKey={zapiKey}
            connectionUrl={connectionUrl}
          />
        )}
        {tabNumber === 4 && (
          <HistoricalData
            orgId={orgId}
            zohoApiKey={apiKey}
            dataCenterUrl={dataCenterUrl}
            zapiKey={zapiKey}
            connectionUrl={connectionUrl}
          />
        )}
      </Box>
    </Box>
  );
}
